import { isNil } from 'lodash';
import { AutoCellTypeObject, CellData, CellTypeObject } from '../../../../../../common/table/TableTypes';
import { CellTypeConstants, CellTypeNames, FormatOptions } from '../../../../../../common/table/CellTypeConstants';
import { isFormulaCell } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { isFormula } from '../formula/tableFormulaPredicateUtils';

export const getDefaultTypeObject = <T extends CellTypeObject>(cellTypeName: CellTypeNames): T => {
    const { formattingOptions = {} } = CellTypeConstants[cellTypeName];
    const defaultOptions: { [key: string]: number | boolean | string | null } = { name: cellTypeName };

    // Loop through the formatting options and set the default value for each
    (Object.keys(formattingOptions) as Array<FormatOptions>).forEach((option) => {
        const value = formattingOptions[option]?.defaultValue;
        if (value === undefined) return;
        defaultOptions[option] = value;
    });
    // Necessary to cast to CellTypeObject object here because it will only match the type after the foreach loop
    return defaultOptions as T;
};

export const cellCanBeRichFormatted = (cellData: CellData): boolean => {
    return !isFormulaCell(cellData) && (!cellData.type || cellData.type.name === CellTypeNames.TEXT);
};

/**
 * Returns whether a cell should be treated as read only in the grid.
 */
export const cellIsReadOnly = (cellData: CellData | undefined): boolean => {
    if (!cellData || !cellData.type) return false;

    return CellTypeConstants[cellData.type.name]?.allowCellEditing === false && !isFormula(cellData.value);
};

/**
 * Collapses selected type objects down to the format values they all share.
 */
export const getSharedFormattingValuesFromCellTypes = (
    selectedCellsTypes: Array<CellTypeObject | undefined>,
): Partial<CellTypeObject> | AutoCellTypeObject | undefined => {
    if (selectedCellsTypes.length === 0) return;
    if (selectedCellsTypes.length === 1) return selectedCellsTypes[0] || { name: CellTypeNames.AUTO };

    const sharedCellFormat: { [key: string]: string | number | boolean } = {};

    const names = new Set<CellTypeNames>();
    selectedCellsTypes.forEach((typeObject) => {
        names.add(typeObject?.name || CellTypeNames.AUTO);
    });

    if (names.size === 1) {
        sharedCellFormat.name = [...names][0];
    }

    Object.values(FormatOptions).forEach((option) => {
        const values = new Set<string | number | boolean>();

        selectedCellsTypes.forEach((typeObject) => {
            if (!typeObject) return;

            const value = typeObject[option as unknown as keyof CellTypeObject];
            if (!isNil(value)) values.add(value);
        });

        if (values.size === 1) {
            sharedCellFormat[option] = [...values][0];
        }
    });

    return sharedCellFormat;
};
